/**
 * Stable codes for every refusal of an AR receipt reconciliation (ALC-4235, slice ccy-s3-ar).
 *
 * Two producers raise them:
 *
 * - the AR receipt reconciler (`packages/services/.../reconcilers/ar-receipt.service.ts`), which
 *   refuses before any receipt row is written; and
 * - the AR receipt GL posting, whose refusal travels as `AR_RECEIPT_GL_POSTING_BLOCKED:<reason>`
 *   with the raw `reason` stored on the FAILED posting row. `arReceiptPostingReasonCode` maps that
 *   reason to the code below; a reason with nothing specific to say maps to the umbrella
 *   `AR_RECEIPT_GL_POSTING_BLOCKED`.
 *
 * Every code here is part of `RECONCILIATION_GL_POSTING_ERROR_CODES`, and the messages record is
 * total (not `Partial`), so a new code without pt-BR copy does not compile. Callers classify by
 * the code, never by the message text.
 */

export const AR_RECEIPT_REFUSAL_CODES = [
  // --- Reconciler refusals (before any receipt is written) ---
  'AR_RECEIPT_BANK_TRANSACTION_ACCOUNT_MISMATCH',
  'AR_RECEIPT_BANK_TRANSACTION_DIRECTION_INVALID',
  'AR_RECEIPT_BANK_ACCOUNT_ENTITY_MISMATCH',
  'AR_RECEIPT_CASH_CURRENCY_UNRESOLVED',
  'AR_RECEIPT_FUNCTIONAL_CURRENCY_UNRESOLVED',
  'AR_RECEIPT_CURRENCY_UNRESOLVED',
  'AR_RECEIPT_CURRENCY_MISMATCH',
  'AR_RECEIPT_FOREIGN_UNITS_REQUIRED',
  'AR_FOREIGN_MULTI_LINK_CASH_ALLOCATION_REQUIRED',
  'AR_RECEIPT_CASH_AMOUNT_INVALID',
  'AR_RECEIPT_MATCHED_UNITS_INVALID',
  'AR_RECEIPT_MATCHED_SCHEDULE_DUPLICATED',
  'AR_RECEIPT_MATCHED_SCHEDULE_NOT_FOUND',
  'AR_RECEIPT_MATCHED_SCHEDULE_AMOUNT_INVALID',
  'AR_RECEIPT_MATCHED_SCHEDULE_ALREADY_SETTLED',
  'AR_RECEIPT_MATCHED_SCHEDULE_BALANCE_STALE',
  'AR_RECEIPT_MATCHED_SCHEDULE_AMOUNT_STALE',
  'AR_INVOICE_STATUS_NOT_RECEIVABLE',
  'AR_DEBIT_NOTE_NOT_FOUND',
  'AR_DEBIT_NOTE_STATUS_NOT_RECEIVABLE',
  'AR_DEBIT_NOTE_NO_REMAINING_BALANCE',
  // --- GL posting refusals (`AR_RECEIPT_GL_POSTING_BLOCKED:<reason>`) ---
  'AR_RECEIPT_GL_POSTING_BLOCKED',
  'AR_RECEIPT_THIRD_CURRENCY_UNSUPPORTED',
  'OVERPAYMENT_EXCEEDS_ALLOCATION',
  'RECEIPT_TARGET_CURRENCY_MISMATCH',
  'RECEIPT_CURRENCY_CONVERSION_REQUIRED',
  'AR_FOREIGN_SETTLEMENT_ADJUSTMENT_DECOMPOSITION_REQUIRED',
  'AR_EXPOSURE_NOT_FOUND',
  'AR_FX_CONFIGURED_CATEGORY_NOT_SUPPORTED',
  'AR_RECEIPT_NOT_FOUND_FOR_SETTLEMENT',
  'AR_RECEIPT_SETTLEMENT_RATE_MISSING',
  'AR_RECEIPT_ALLOCATION_MISSING',
  'AR_RECEIPT_CLEARING_AMOUNT_INVALID',
] as const;

export type ArReceiptRefusalCode = (typeof AR_RECEIPT_REFUSAL_CODES)[number];

export function isArReceiptRefusalCode(value: unknown): value is ArReceiptRefusalCode {
  return (
    typeof value === 'string' && (AR_RECEIPT_REFUSAL_CODES as readonly string[]).includes(value)
  );
}

/**
 * The AR GL posting's raw refusal reasons whose code is named differently. Every other reason is
 * either a code itself (`AR_EXPOSURE_NOT_FOUND`, `OVERPAYMENT_EXCEEDS_ALLOCATION`, …) or has
 * nothing the user can act on beyond "this receipt was not posted", and maps to the umbrella
 * `AR_RECEIPT_GL_POSTING_BLOCKED` with the reason kept alongside it.
 */
const AR_RECEIPT_POSTING_REASON_ALIASES: Readonly<Record<string, ArReceiptRefusalCode>> = {
  MISSING_SETTLEMENT_RATE: 'AR_RECEIPT_SETTLEMENT_RATE_MISSING',
  NO_RECEIPT_ALLOCATION: 'AR_RECEIPT_ALLOCATION_MISSING',
  RECEIVABLE_CLEARING_AMOUNT_INVALID: 'AR_RECEIPT_CLEARING_AMOUNT_INVALID',
  INVOICE_BANK_ENTITY_MISMATCH: 'AR_RECEIPT_BANK_ACCOUNT_ENTITY_MISMATCH',
  DEBIT_NOTE_BANK_ENTITY_MISMATCH: 'AR_RECEIPT_BANK_ACCOUNT_ENTITY_MISMATCH',
  BANK_TRANSACTION_ACCOUNT_MISMATCH: 'AR_RECEIPT_BANK_TRANSACTION_ACCOUNT_MISMATCH',
  BANK_TRANSACTION_DIRECTION_INVALID: 'AR_RECEIPT_BANK_TRANSACTION_DIRECTION_INVALID',
  BANK_TRANSACTION_ACCOUNT_CURRENCY_UNRESOLVED: 'AR_RECEIPT_CASH_CURRENCY_UNRESOLVED',
  DEBIT_NOTE_NOT_FOUND: 'AR_DEBIT_NOTE_NOT_FOUND',
};

/**
 * The stable code for one AR GL posting refusal reason, as stored on the FAILED posting row.
 * Reads the stem before the first `:` so a reason carrying a suffix still classifies.
 */
export function arReceiptPostingReasonCode(reason: string): ArReceiptRefusalCode {
  const stem = reason.split(':', 1)[0] ?? reason;
  const alias = AR_RECEIPT_POSTING_REASON_ALIASES[stem];
  if (alias) return alias;
  return isArReceiptRefusalCode(stem) ? stem : 'AR_RECEIPT_GL_POSTING_BLOCKED';
}

const NOTHING_POSTED_EN = 'Nothing was posted.';
const NOTHING_POSTED_PT = 'Nada foi contabilizado.';

/** Localized, actionable copy for every AR receipt refusal. Total by construction. */
export const AR_RECEIPT_REFUSAL_MESSAGES: Record<
  ArReceiptRefusalCode,
  { en: string; ptBr: string }
> = {
  AR_RECEIPT_BANK_TRANSACTION_ACCOUNT_MISMATCH: {
    en: `The bank movement no longer belongs to the bank account of this reconciliation. Reload the queue and reconcile again. ${NOTHING_POSTED_EN}`,
    ptBr: `A movimentação bancária não pertence mais à conta desta conciliação. Atualize a fila e concilie de novo. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_BANK_TRANSACTION_DIRECTION_INVALID: {
    en: `Only a bank credit (money in) can settle a receivable. Pick a credit movement. ${NOTHING_POSTED_EN}`,
    ptBr: `Só uma entrada no banco (crédito) pode baixar um título a receber. Escolha uma movimentação de crédito. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_BANK_ACCOUNT_ENTITY_MISMATCH: {
    en: 'The bank movement and the receivable must belong to the same entity to be reconciled.',
    ptBr: 'A transação bancária e o título a receber devem pertencer à mesma entidade para serem conciliados.',
  },
  AR_RECEIPT_CASH_CURRENCY_UNRESOLVED: {
    en: `The bank account's currency is not confirmed. Confirm it in Settings → Accounts, then reconcile again. ${NOTHING_POSTED_EN}`,
    ptBr: `A moeda da conta bancária não está confirmada. Confirme a moeda em Configurações → Contas e concilie de novo. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_FUNCTIONAL_CURRENCY_UNRESOLVED: {
    en: `The entity's books do not agree on one functional currency. Review the entity's books before reconciling. ${NOTHING_POSTED_EN}`,
    ptBr: `Os livros da entidade não têm uma única moeda funcional. Revise os livros da entidade antes de conciliar. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_CURRENCY_UNRESOLVED: {
    en: `The currency of the receivable or of the bank movement could not be identified for posting. Review both currencies. ${NOTHING_POSTED_EN}`,
    ptBr: `Não foi possível identificar a moeda do título ou da movimentação bancária para contabilizar. Revise as duas moedas. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_CURRENCY_MISMATCH: {
    en: `The money arrived in a currency that is neither the receivable's currency nor the company's functional currency. ${NOTHING_POSTED_EN}`,
    ptBr: `O dinheiro entrou em uma moeda que não é a do título nem a moeda funcional da empresa. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_FOREIGN_UNITS_REQUIRED: {
    en: `This foreign-currency receivable was received in local currency. Enter how much of the receivable, in its own currency, this receipt settles. ${NOTHING_POSTED_EN}`,
    ptBr: `Este título em moeda estrangeira foi recebido em moeda local. Informe quanto do título, na moeda dele, este recebimento baixa. ${NOTHING_POSTED_PT}`,
  },
  AR_FOREIGN_MULTI_LINK_CASH_ALLOCATION_REQUIRED: {
    en: `One local-currency receipt cannot settle several foreign-currency receivables at once yet. Reconcile each receivable with its own receipt. ${NOTHING_POSTED_EN}`,
    ptBr: `Um recebimento em moeda local ainda não pode baixar vários títulos em moeda estrangeira de uma vez. Concilie cada título com o seu recebimento. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_CASH_AMOUNT_INVALID: {
    en: `The bank movement has no positive amount to settle the receivable with. Review the statement line. ${NOTHING_POSTED_EN}`,
    ptBr: `A movimentação bancária não tem valor positivo para baixar o título. Revise a linha do extrato. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_MATCHED_UNITS_INVALID: {
    en: `The amount of the receivable settled by this receipt must be greater than zero. ${NOTHING_POSTED_EN}`,
    ptBr: `O valor do título baixado por este recebimento deve ser maior que zero. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_MATCHED_SCHEDULE_DUPLICATED: {
    en: `The same installment was selected twice. Select each installment once. ${NOTHING_POSTED_EN}`,
    ptBr: `A mesma parcela foi selecionada duas vezes. Selecione cada parcela uma única vez. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_MATCHED_SCHEDULE_NOT_FOUND: {
    en: `An installment selected for this receipt no longer belongs to the receivable. Reload and select the installments again. ${NOTHING_POSTED_EN}`,
    ptBr: `Uma parcela selecionada não pertence mais ao título. Atualize e selecione as parcelas de novo. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_MATCHED_SCHEDULE_AMOUNT_INVALID: {
    en: `Each selected installment needs an amount greater than zero. ${NOTHING_POSTED_EN}`,
    ptBr: `Cada parcela selecionada precisa de um valor maior que zero. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_MATCHED_SCHEDULE_ALREADY_SETTLED: {
    en: `An installment selected for this receipt is already fully received. Reload and select the open installments. ${NOTHING_POSTED_EN}`,
    ptBr: `Uma parcela selecionada já foi totalmente recebida. Atualize e selecione as parcelas em aberto. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_MATCHED_SCHEDULE_BALANCE_STALE: {
    en: `An installment's open balance changed since it was matched. Reload and match the installments again. ${NOTHING_POSTED_EN}`,
    ptBr: `O saldo em aberto de uma parcela mudou desde a seleção. Atualize e selecione as parcelas de novo. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_MATCHED_SCHEDULE_AMOUNT_STALE: {
    en: `The installments selected no longer add up to the amount being received. Reload and match the installments again. ${NOTHING_POSTED_EN}`,
    ptBr: `As parcelas selecionadas não somam mais o valor recebido. Atualize e selecione as parcelas de novo. ${NOTHING_POSTED_PT}`,
  },
  AR_INVOICE_STATUS_NOT_RECEIVABLE: {
    en: 'This receivable is not released for receipt yet. Finish issuing the invoice and leave it as Issued before reconciling it.',
    ptBr: 'Este contas a receber ainda não está liberado para recebimento. Conclua a emissão da nota fiscal e deixe o título como Emitido antes de conciliá-lo.',
  },
  AR_DEBIT_NOTE_NOT_FOUND: {
    en: `The debit note of this reconciliation was not found. Reload the queue and reconcile again. ${NOTHING_POSTED_EN}`,
    ptBr: `A nota de débito desta conciliação não foi encontrada. Atualize a fila e concilie de novo. ${NOTHING_POSTED_PT}`,
  },
  AR_DEBIT_NOTE_STATUS_NOT_RECEIVABLE: {
    en: `Only an issued or partially received debit note can be reconciled. Review the debit note's status. ${NOTHING_POSTED_EN}`,
    ptBr: `Só uma nota de débito emitida ou parcialmente recebida pode ser conciliada. Revise o status da nota de débito. ${NOTHING_POSTED_PT}`,
  },
  AR_DEBIT_NOTE_NO_REMAINING_BALANCE: {
    en: `This debit note has no open balance left to receive. ${NOTHING_POSTED_EN}`,
    ptBr: `Esta nota de débito não tem saldo em aberto para receber. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_GL_POSTING_BLOCKED: {
    en: `The accounting entry of this receipt could not be posted. Review the receivable and the bank movement, then reconcile again. ${NOTHING_POSTED_EN}`,
    ptBr: `Não foi possível contabilizar este recebimento. Revise o título e a movimentação bancária e concilie de novo. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_THIRD_CURRENCY_UNSUPPORTED: {
    en: `This receipt is in a currency that is neither the receivable's nor the company's functional currency, which is not supported. ${NOTHING_POSTED_EN}`,
    ptBr: `Este recebimento está em uma moeda que não é a do título nem a funcional da empresa, o que não é suportado. ${NOTHING_POSTED_PT}`,
  },
  OVERPAYMENT_EXCEEDS_ALLOCATION: {
    en: `The amount received is above what this receivable allows to settle. Review the amounts or record the excess separately. ${NOTHING_POSTED_EN}`,
    ptBr: `O valor recebido é maior do que este título permite baixar. Revise os valores ou registre o excedente separadamente. ${NOTHING_POSTED_PT}`,
  },
  RECEIPT_TARGET_CURRENCY_MISMATCH: {
    en: `The receipt's currency does not match the receivable's currency. ${NOTHING_POSTED_EN}`,
    ptBr: `A moeda do recebimento não confere com a moeda do título. ${NOTHING_POSTED_PT}`,
  },
  RECEIPT_CURRENCY_CONVERSION_REQUIRED: {
    en: `This receipt needs a currency conversion that was not provided. Enter the settled amount in the receivable's currency. ${NOTHING_POSTED_EN}`,
    ptBr: `Este recebimento precisa de uma conversão de moeda que não foi informada. Informe o valor baixado na moeda do título. ${NOTHING_POSTED_PT}`,
  },
  AR_FOREIGN_SETTLEMENT_ADJUSTMENT_DECOMPOSITION_REQUIRED: {
    en: `A foreign-currency receivable received in local currency cannot carry withheld tax, interest, fine or discount yet. Do not remove the adjustments: that would leave the withheld tax unrecognised and overstate the exchange gain. Receive it in the receivable's currency, or ask your accountant to book the withholding manually. ${NOTHING_POSTED_EN}`,
    ptBr: `Um título em moeda estrangeira recebido em moeda local ainda não pode ter imposto retido, juros, multa ou desconto. Não remova os ajustes: o imposto retido deixaria de ser reconhecido e a variação cambial ficaria superestimada. Receba na moeda do título ou peça ao contador para lançar a retenção manualmente. ${NOTHING_POSTED_PT}`,
  },
  AR_EXPOSURE_NOT_FOUND: {
    en: `This foreign-currency receivable has no open currency exposure to settle. Review the receivable's issue entry. ${NOTHING_POSTED_EN}`,
    ptBr: `Este título em moeda estrangeira não tem exposição cambial em aberto para baixar. Revise o lançamento de emissão do título. ${NOTHING_POSTED_PT}`,
  },
  AR_FX_CONFIGURED_CATEGORY_NOT_SUPPORTED: {
    en: `The exchange-rate category configured for this receivable is not supported for receipts. Review the currency settings. ${NOTHING_POSTED_EN}`,
    ptBr: `A categoria de câmbio configurada para este título não é suportada no recebimento. Revise a configuração de câmbio. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_NOT_FOUND_FOR_SETTLEMENT: {
    en: `The receipt of this reconciliation was not found when posting. Reload the queue and reconcile again. ${NOTHING_POSTED_EN}`,
    ptBr: `O recebimento desta conciliação não foi encontrado na contabilização. Atualize a fila e concilie de novo. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_SETTLEMENT_RATE_MISSING: {
    en: `There is no exchange rate for the receipt date. Register the rate for that day, then reconcile again. ${NOTHING_POSTED_EN}`,
    ptBr: `Não há cotação de câmbio para a data do recebimento. Cadastre a cotação desse dia e concilie de novo. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_ALLOCATION_MISSING: {
    en: `This receipt has no amount allocated to the receivable. Review the reconciliation amounts. ${NOTHING_POSTED_EN}`,
    ptBr: `Este recebimento não tem valor alocado ao título. Revise os valores da conciliação. ${NOTHING_POSTED_PT}`,
  },
  AR_RECEIPT_CLEARING_AMOUNT_INVALID: {
    en: `The amount of the receivable settled by this receipt is invalid. Review the reconciliation amounts. ${NOTHING_POSTED_EN}`,
    ptBr: `O valor do título baixado por este recebimento é inválido. Revise os valores da conciliação. ${NOTHING_POSTED_PT}`,
  },
};
